/**
 * Partial Repayment Results Display Component
 * 部分還款結果顯示組件
 */
import { useCustomTranslation } from '../../../hooks';
import { formatCurrency } from '../../../utils/formatters';
import { trackPaymentScheduleView } from '../../../utils/analytics';

export interface PaymentScheduleItem {
  payment_number: number;
  payment_amount: number;
  principal_component: number;
  interest_component: number;
  remaining_balance: number;
}

export interface PartialRepaymentResultsData {
  original_outstanding_balance: number;
  original_remaining_instalments: number;
  original_instalment_amount: number;
  original_total_remaining_payment: number;
  original_total_interest: number;
  
  new_outstanding_balance: number;
  new_remaining_instalments: number;
  new_instalment_amount: number;
  new_total_remaining_payment: number;
  new_total_interest: number;
  
  interest_saved: number;
  total_saved: number;
  instalments_reduced: number;
  instalment_amount_reduced: number;
  
  partial_repayment_amount: number;
  frequency: string;
  annual_interest_rate: number;
  
  payment_schedule?: PaymentScheduleItem[];
}

interface PartialRepaymentResultsProps {
  results: PartialRepaymentResultsData | null;
  onViewSchedule: () => void;
}

export default function PartialRepaymentResults({ results, onViewSchedule }: PartialRepaymentResultsProps) {
  const { t } = useCustomTranslation();

  if (!results) {
    return (
      <div className="results-display placeholder">
        <p>{t('results.placeholder')}</p>
      </div>
    );
  }

  const isReducePeriod = results.instalments_reduced > 0;
  const hasSchedule = results.payment_schedule && results.payment_schedule.length > 0;

  const handleViewSchedule = () => {
    trackPaymentScheduleView('partial_repayment');
    onViewSchedule();
  };

  return (
    <div className="results-display">
      {/* Savings Summary */}
      <div className="savings-summary">
        <h3>{t('partialRepayment.savingsSummary')}</h3>
        <div className="savings-cards">
          <div className="result-card highlight">
            <div className="result-label">{t('partialRepayment.interestSaved')}</div>
            <div className="result-value">{formatCurrency(results.interest_saved)}</div>
          </div>
        </div>
      </div>

      {/* Comparison Table */}
      <div className="comparison-table">
        <h3>{t('partialRepayment.comparison')}</h3>
        <table style={{marginTop: '10px'}}>
          <thead>
            <tr>
              <th></th>
              <th>{t('partialRepayment.beforeRepayment')}</th>
              <th>{t('partialRepayment.afterRepayment')}</th>
              <th>{t('partialRepayment.difference')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('partialRepayment.outstandingBalance')}</td>
              <td>{formatCurrency(results.original_outstanding_balance)}</td>
              <td>{formatCurrency(results.new_outstanding_balance)}</td>
              <td className="positive">{formatCurrency(results.partial_repayment_amount)}</td>
            </tr>
            <tr>
              <td>{t('partialRepayment.remainingInstalments')}</td>
              <td>{results.original_remaining_instalments}</td>
              <td>{results.new_remaining_instalments}</td>
              <td className={isReducePeriod ? 'positive' : ''}>
                {isReducePeriod ? `-${results.instalments_reduced}` : '—'}
              </td>
            </tr>
            <tr>
              <td>{t('partialRepayment.instalmentAmount')}</td>
              <td>{formatCurrency(results.original_instalment_amount)}</td>
              <td>{formatCurrency(results.new_instalment_amount)}</td>
              <td className={!isReducePeriod ? 'positive' : ''}>
                {!isReducePeriod ? `-${formatCurrency(results.instalment_amount_reduced)}` : '—'}
              </td>
            </tr>
            <tr>
              <td>{t('partialRepayment.totalRemainingPayment')}</td>
              <td>{formatCurrency(results.original_total_remaining_payment)}</td>
              <td>{formatCurrency(results.new_total_remaining_payment)}</td>
              <td className="positive">-{formatCurrency(results.total_saved)}</td>
            </tr>
            <tr>
              <td>{t('partialRepayment.totalInterest')}</td>
              <td>{formatCurrency(results.original_total_interest)}</td>
              <td>{formatCurrency(results.new_total_interest)}</td>
              <td className="positive">-{formatCurrency(results.interest_saved)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Details Summary */}
      <div className="details-summary">
        <h3>{t('partialRepayment.details')}</h3>
        <div className="summary-row">
          <span>{t('partialRepayment.partialAmount')}:</span>
          <span>{formatCurrency(results.partial_repayment_amount)}</span>
        </div>
        <div className="summary-row">
          <span>{t('partialRepayment.interestRate')}:</span>
          <span>{results.annual_interest_rate}%</span>
        </div>
        <div className="summary-row">
          <span>{t('partialRepayment.repaymentPlan')}:</span>
          <span>{t(`frequency.${results.frequency}`)}</span>
        </div>
        {isReducePeriod ? (
          <div className="summary-row highlight">
            <span>{t('partialRepayment.periodReduced')}:</span>
            <span>
              {results.instalments_reduced} {t('partialRepayment.instalments')}
              {(() => {
                const paymentsPerYear = results.frequency === 'monthly' ? 12 : 26;
                const totalYears = results.instalments_reduced / paymentsPerYear;
                const years = Math.floor(totalYears);
                const remainingPayments = results.instalments_reduced - (years * paymentsPerYear);
                const months = Math.round((remainingPayments / paymentsPerYear) * 12);
                
                if (years > 0 && months > 0) {
                  return ` (${years} ${t('results.summary.years')} ${months} ${t('partialRepayment.months')})`;
                } else if (years > 0) {
                  return ` (${years} ${t('results.summary.years')})`;
                } else {
                  return ` (${months} ${t('partialRepayment.months')})`;
                }
              })()}
            </span>
          </div>
        ) : (
          <div className="summary-row highlight">
            <span>{t('partialRepayment.instalmentReduced')}:</span>
            <span>{formatCurrency(results.instalment_amount_reduced)}</span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {hasSchedule && (
        <div className="action-buttons" style={{ marginTop: '20px' }}>
          <button onClick={handleViewSchedule} className="primary">
            {t('partialRepayment.viewSchedule')}
          </button>
        </div>
      )}
    </div>
  );
}
